import React from 'react';
import { MessageCircle, Phone, Calendar, Info, Users, Mountain, Trees, Home } from 'lucide-react';
import { homestayInfo } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';

const Availability = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi, I checked availability on your website and would like to book a stay at White Orchid Homestay.");
    window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:${homestayInfo.phone}`;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Check Availability</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            View our real-time room availability and get in touch to book your stay
          </p>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-8 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <Alert className="max-w-4xl mx-auto bg-white border-[#A3B18A]">
            <Info className="h-5 w-5 text-[#1F4D3A]" />
            <AlertDescription className="text-gray-700 ml-2">
              <strong>How to Book:</strong> The calendar below shows all 4 rooms together. Each colored bar represents a specific room. If you see a colored bar on your desired dates, that particular room is booked, but <strong>other rooms may still be available!</strong> Check the room cards below and contact us directly.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#1F4D3A] mb-4">Room Availability</h2>
              <p className="text-gray-600">Blocked dates are already booked or unavailable</p>
            </div>

            {/* Google Calendar Embed Container */}
            <div className="bg-[#F7F5F0] rounded-lg p-4 md:p-8 shadow-lg">
              <div className="calendar-container" style={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                <iframe
                  src="https://calendar.google.com/calendar/embed?height=600&wkst=1&ctz=Asia%2FKolkata&showPrint=0&showTitle=0&showTabs=0&showCalendars=0&showTz=0&src=NzhlYzBiNjI1OWM4YWU1ZjIwOTdmNGVmYzA0ZTE3NjllZjRkNThlZWM3NmExMTFjMDhmNjRmMTI3OTg3YjQ4ZkBncm91cC5jYWxlbmRhci5nb29nbGUuY29t&src=N2U3MDk5YzE0NmIxOGVmMTU2ZDZhMmJlOWE2MTBkMzhmMjYwNWNkOWFjODBmNWZkYWVhY2ZiMDc2YThmYzY1N0Bncm91cC5jYWxlbmRhci5nb29nbGUuY29t&src=NWMzZDUxNzU1Y2IxYTc2YmM2MmIyMzc1OWEyNDA0NTc3ZDkxNjYwMGJiYjk0ZDkyNjcwOWFmMTBhYWIxZjZmNUBncm91cC5jYWxlbmRhci5nb29nbGUuY29t&src=MDhjNzRlMjZiMzMzMWEwNTVjNjdiYjI3Mjc4YzVkMDYyNmMzNmU2ZmU5YTNhNDdlZTEwNTQ1MmQ1NzVjZGU2MEBncm91cC5jYWxlbmRhci5nb29nbGUuY29t&color=%2333b679&color=%234285f4&color=%239e69af&color=%23ef6c00"
                  style={{ border: '1px solid #ddd', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
                  frameBorder="0"
                  scrolling="no"
                  title="White Orchid Homestay Availability Calendar - All Rooms"
                ></iframe>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-[#1F4D3A] mb-4 text-center">Room Color Legend</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded"></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Room 1</p>
                    <p className="text-xs text-gray-600">2 persons</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded"></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Room 2 ⭐</p>
                    <p className="text-xs text-gray-600">4 persons</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-purple-500 rounded"></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Room 3</p>
                    <p className="text-xs text-gray-600">2 persons</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-orange-500 rounded"></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Room 4</p>
                    <p className="text-xs text-gray-600">4 persons</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Check Availability by Room */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1F4D3A] mb-4">Check Availability by Room</h2>
              <p className="text-lg text-gray-600">Select a room and contact us directly to check availability for your dates</p>
            </div>

            {/* Room Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Room 1 */}
              <Card className="overflow-hidden hover:shadow-xl transition-all border-2 hover:border-green-500">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peaceful-orchid/artifacts/qndveym1_IMG-20251123-WA0027.jpg"
                    alt="Room 1"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    Room 1
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#1F4D3A] mb-4">Room No-1</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="w-5 h-5 text-green-500" />
                      <span><strong>Capacity:</strong> 2 persons (1 double bed)</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Trees className="w-5 h-5 text-green-500" />
                      <span><strong>View:</strong> Limited pine forest view</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const message = encodeURIComponent(
                        "Hi, I'd like to check availability and book Room 1 (2 persons, limited pine forest view) at White Orchid Homestay."
                      );
                      window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                    }}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Check & Book Room 1
                  </Button>
                </CardContent>
              </Card>

              {/* Room 2 */}
              <Card className="overflow-hidden hover:shadow-xl transition-all border-2 hover:border-blue-500 ring-2 ring-[#D4A373]">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peaceful-orchid/artifacts/sy4e3u8f_IMG-20251123-WA0025.jpg"
                    alt="Room 2"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                    Room 2 ⭐
                  </div>
                  <div className="absolute top-4 right-4 bg-[#D4A373] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Best View
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#1F4D3A] mb-4">Room No-2</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="w-5 h-5 text-blue-500" />
                      <span><strong>Capacity:</strong> 4 persons (2 double beds)</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mountain className="w-5 h-5 text-blue-500" />
                      <span><strong>View:</strong> Spectacular Mt. Kanchenjunga + pine forest</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const message = encodeURIComponent(
                        "Hi, I'd like to check availability and book Room 2 (4 persons, spectacular Mt. Kanchenjunga + pine forest view) at White Orchid Homestay."
                      );
                      window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                    }}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6 text-lg"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Check & Book Room 2
                  </Button>
                </CardContent>
              </Card>

              {/* Room 3 */}
              <Card className="overflow-hidden hover:shadow-xl transition-all border-2 hover:border-purple-500">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peaceful-orchid/artifacts/zakjz4z0_IMG-20251123-WA0032.jpg"
                    alt="Room 3"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full border-2 border-white"></div>
                    Room 3
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#1F4D3A] mb-4">Room No-3</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="w-5 h-5 text-purple-500" />
                      <span><strong>Capacity:</strong> 2 persons (1 double bed)</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mountain className="w-5 h-5 text-purple-500" />
                      <span><strong>View:</strong> Spectacular Mt. Kanchenjunga view</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const message = encodeURIComponent(
                        "Hi, I'd like to check availability and book Room 3 (2 persons, spectacular Mt. Kanchenjunga view) at White Orchid Homestay."
                      );
                      window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                    }}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white py-6 text-lg"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Check & Book Room 3
                  </Button>
                </CardContent>
              </Card>

              {/* Room 4 */}
              <Card className="overflow-hidden hover:shadow-xl transition-all border-2 hover:border-orange-500">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_peaceful-orchid/artifacts/5gl9884s_IMG-20251123-WA0026.jpg"
                    alt="Room 4"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full border-2 border-white"></div>
                    Room 4
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-[#1F4D3A] mb-4">Room No-4</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="w-5 h-5 text-orange-500" />
                      <span><strong>Capacity:</strong> 4 persons (2 double beds)</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Home className="w-5 h-5 text-orange-500" />
                      <span><strong>View:</strong> No view (cozy interior room)</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      const message = encodeURIComponent(
                        "Hi, I'd like to check availability and book Room 4 (4 persons, no view) at White Orchid Homestay."
                      );
                      window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                    }}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Check & Book Room 4
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Not Sure Which Room? Contact Us!</h2>
            <p className="text-xl text-white/90 mb-8">
              We'll help you choose the perfect room for your stay
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-2xl mx-auto">
              {/* WhatsApp Button */}
              <Button
                size="lg"
                onClick={() => {
                  const message = encodeURIComponent("Hi, I'd like to check availability at White Orchid Homestay. Can you help me choose the right room?");
                  window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                }}
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-6 text-lg flex-1"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>

              {/* Call Button */}
              <Button
                size="lg"
                onClick={handleCallClick}
                className="bg-white text-[#1F4D3A] hover:bg-white/90 px-8 py-6 text-lg flex-1"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call {homestayInfo.phone}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-12 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#1F4D3A] mb-6 text-center">Booking Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold text-[#1F4D3A] mb-3">Check-in / Check-out</h4>
                  <p className="text-gray-700 text-sm mb-2">Check-in: 12:00 PM</p>
                  <p className="text-gray-700 text-sm mb-2">Check-out: 11:00 AM</p>
                  <p className="text-xs text-gray-600 italic mt-2">(Flexible timing available on request)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold text-[#1F4D3A] mb-3">Booking Policy</h4>
                  <p className="text-gray-700 text-sm mb-2">Advance booking recommended</p>
                  <p className="text-gray-700 text-sm mb-2">Confirmation via WhatsApp/Call</p>
                  <p className="text-xs text-gray-600 italic mt-2">Cancellation policy applies</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Availability;
